<script lang="ts">
  /** THE DECISION TREE (G6b): one choice it made, every branch — each object it could think of: SCORED by the drives it serves
   *  (level/100 × what the object satisfies × the drive's weight, less 1.5 a metre to walk there), or PRUNED and why (not on its layers ·
   *  unknown to it · nothing it needs · busy · in someone's hands · not usable now · players only). The chosen branch is lit; the
   *  minimum a choice must reach is drawn. Its last choices are dots to step through (the newest selected); `sel` (a decision's time)
   *  is shared with the trace, so a node clicked there opens here. */
  import type { Decision } from '../types'
  let { decisions = [], sel = $bindable(null) }: { decisions?: Decision[]; sel?: string | null } = $props()

  // the one picked (by its time — the list shifts as new ones arrive), else the newest
  const d = $derived(decisions.find((x) => x.at === sel) ?? decisions[decisions.length - 1] ?? null)
  const top = $derived(d ? Math.max(d.min, ...d.branches.map((b) => b.score ?? 0)) : 1)
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / (top || 1)) * 100)).toFixed(1)}%`
  const hhmmss = (iso: string) => new Date(iso).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const n = (v: number) => (Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2)).replace(/\.?0+$/, '')
</script>

{#if !d}
  <p class="muted">No decisions yet — it decides each time its graph says <b>choose</b>; a hosted NPC's arrive with its readout.</p>
{:else}
  <div class="dots" role="tablist" aria-label="Its last decisions">
    {#each decisions as x, i}
      <button class="pick" class:sel={x === d} class:none={!x.chose} title={`${hhmmss(x.at)} — ${x.chose ?? 'nothing'}`} onclick={() => (sel = i === decisions.length - 1 ? null : x.at)} aria-label={`Decision at ${hhmmss(x.at)}: ${x.chose ?? 'nothing'}`}></button>
    {/each}
  </div>
  <div class="root mono">
    <b>CHOOSE</b> <span class="muted">· {hhmmss(d.at)} · in {d.state} · must reach {d.min}</span>
    <span class="res" class:none={!d.chose}>{d.chose ? `→ ${d.chose} (${n(d.score ?? 0)})` : `→ nothing (best ${d.score != null ? n(d.score) : '—'})`}</span>
  </div>
  <ul class="tree">
    {#each d.branches as b (b.name)}
      {@const chosen = b.name === d.chose}
      <li class:chosen class:pruned={!!b.pruned} class:low={b.score != null && b.score < d.min}>
        <div class="row">
          <span class="nm">{b.name}<small>{b.kind}</small></span>
          {#if b.pruned}
            <span class="why">✕ {b.pruned}</span>
          {:else}
            <span class="bar"><i style={`width:${pct(b.score ?? 0)}`}></i><em style={`left:${pct(d.min)}`}></em></span>
            <span class="sc mono">{n(b.score ?? 0)}</span>
          {/if}
          {#if chosen}<span class="tag">CHOSEN</span>{/if}
        </div>
        {#if !b.pruned}
          <div class="parts mono">
            {#each b.parts ?? [] as p}<span>{p.drive} {n(p.level)}/100 × {n(p.amount)} × {n(p.weight)} = <b>{n(p.value)}</b></span>{/each}
            <span class="walk">walk − 1.5 × {n(b.dist ?? 0)} m = <b>{b.dist ? `−${n(1.5 * b.dist)}` : '0'}</b></span>
          </div>
        {/if}
      </li>
    {/each}
  </ul>
{/if}

<style>
  .dots { display: flex; gap: 6px; margin-bottom: 8px; }
  .pick { width: 12px; height: 12px; padding: 0; border-radius: 50%; border: 1px solid var(--line); background: #10171e; cursor: pointer; }
  .pick.none { border-color: var(--amber); }
  .pick.sel { background: var(--cyan); }
  .root { font-size: 12px; padding: 4px 8px; border-left: 2px solid var(--cyan); background: #0e151b; display: flex; gap: 6px; flex-wrap: wrap; align-items: baseline; }
  .root b { color: var(--cyan); }
  .res { margin-left: auto; color: var(--green); }
  .res.none { color: var(--amber); }
  .tree { list-style: none; margin: 0; padding: 0 0 0 14px; border-left: 1px solid var(--line-dim); margin-left: 8px; }
  .tree li { position: relative; padding: 5px 0 5px 10px; }
  .tree li::before { content: ''; position: absolute; left: -14px; top: 15px; width: 20px; border-top: 1px solid var(--line-dim); }
  .row { display: grid; grid-template-columns: minmax(120px, 170px) 1fr 52px auto; gap: 10px; align-items: center; }
  .nm { font-size: 15px; display: flex; flex-direction: column; line-height: 1.1; }
  .nm small { color: var(--faint); font-size: 10.5px; text-transform: uppercase; letter-spacing: .05em; }
  .bar { position: relative; height: 8px; background: #1a232c; }
  .bar i { display: block; height: 100%; background: var(--cyan); opacity: .75; }
  .bar em { position: absolute; top: -3px; bottom: -3px; width: 1px; background: var(--amber); }
  .sc { text-align: right; color: var(--text); }
  .why { grid-column: 2 / 4; color: var(--faint); font-size: 13px; }
  .tag { font: 600 10.5px var(--display); letter-spacing: .08em; color: #02130c; background: var(--green); padding: 1px 6px; }
  .parts { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 10.5px; color: #8aa1ad; padding: 3px 0 0 2px; }
  .parts b { color: var(--text); font-weight: 500; }
  .parts .walk b { color: #ffb3b6; }
  li.chosen .nm { color: var(--green); }
  li.chosen .bar i { background: var(--green); opacity: 1; }
  li.low .bar i { opacity: .35; }
  li.pruned .nm { color: var(--faint); text-decoration: line-through; text-decoration-color: rgba(255, 255, 255, .15); }
  @media (max-width: 700px) { .row { grid-template-columns: 1fr 52px auto; } .row .bar { grid-column: 1 / -1; grid-row: 2; } }
</style>
